// JobModule — MIDNIGHT elevation
//
// Renders the job section inside DetailDrawer.
// §10 glass nesting: uses ModulePanel (ds-tile-inset, no backdrop-filter).
// Header row: SectionHeader with pastel icon chip; action buttons use Button variant="icon".
// All existing callbacks + field reads preserved unchanged.

import JobPunchList from '@/components/Jobs/JobPunchList'
import { Link } from 'react-router-dom'
import { usePermissions } from '@/hooks/usePermission'
import { JobMaterialsLog } from '@/components/Jobs/JobMaterialsLog'
import { CalendarDays, Camera, Package, Users, Briefcase } from 'lucide-react'
import { toneForStatus } from '@/lib/phaseTone'
import type { Record_ } from './types'
import { TONE_DOT_COLOR, type ModuleTone } from './_drawerHelpers'
import { SectionHeader, ModulePanel, GhostButton } from './_drawerHelpers'
import { getInitials } from '@/utils/formatting'
import { JobIconActions } from './DrawerHeaderActions'
import { JobCostingPanel } from '@/components/JobCosting/JobCostingPanel'

interface JobModuleProps {
  record: Record_
  onEditJob?: () => void
  onRescheduleJob?: () => void
  /** False on a phone sheet: Edit renders in the drawer header. */
  inlineActions?: boolean
}

export function JobModule({ record, onEditJob, onRescheduleJob, inlineActions = true }: JobModuleProps) {
  const { can } = usePermissions()
  const isActive = record.stage === 'IN_PROGRESS'
  const isDone   = ['COMPLETED', 'INVOICED', 'PAID'].includes(record.stage)
  // Phase tone of the job's stage: sky while in production, butter once
  // completed / invoiced, mint when paid (lib/phaseTone).
  const accent: ModuleTone = toneForStatus(record.stage, 'job')
  const accentColor = TONE_DOT_COLOR[accent]


  return (
    <ModulePanel accent={accent}>
      {/* Header row: title left, icon action buttons right */}
      <div className="ds-doc-head">
        <SectionHeader
          tone={accent}
          icon={Briefcase}
          title="Job"
          meta={record.jobDuration ? <span>{record.jobDuration}</span> : undefined}
        />
        {inlineActions && <JobIconActions onEditJob={onEditJob} />}
      </div>

      {/* Content */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {/* Schedule / completed date */}
        {(record.scheduledFor || record.completedAt) && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <CalendarDays
              style={{ width: 14, height: 14, color: accentColor, flexShrink: 0 }}
            />
            <span
              style={{ fontSize: 13, color: 'rgb(var(--fg-rgb) / 0.70)' }}
            >
              {isDone
                ? (record.completedAt ? `Finished ${record.completedAt}` : 'Finished')
                : isActive && record.startedAt
                  ? `Started ${record.startedAt}`
                  : record.scheduledFor}
            </span>
          </div>
        )}

        {/* Progress bar — in-progress only */}
        {isActive && record.progressPercent !== undefined && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                fontSize: 13,
              }}
            >
              <span style={{ color: 'var(--midnight-text3)' }}>Progress</span>
              <span
                style={{
                  color: 'rgb(var(--fg-rgb) / 0.70)',
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {record.progressPercent}%
                {record.hoursLogged && record.jobDuration
                  ? ` · ${record.hoursLogged} of ${record.jobDuration}`
                  : ''}
              </span>
            </div>
            <div
              style={{
                height: 4,
                borderRadius: 9999,
                background: 'rgb(var(--fg-rgb) / 0.06)',
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  height: '100%',
                  width: `${record.progressPercent}%`,
                  background: accentColor,
                  borderRadius: 9999,
                  transition: 'width 0.5s ease',
                }}
              />
            </div>
          </div>
        )}

        {/* Crew avatars */}
        {record.crew && record.crew.length > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Users
              style={{ width: 14, height: 14, color: 'var(--midnight-text3)', flexShrink: 0 }}
            />
            <div style={{ display: 'flex', marginLeft: -4 }}>
              {record.crew.map((name) => {
                const initials = getInitials(name)
                return (
                  <span
                    key={name}
                    title={name}
                    style={{
                      width: 24,
                      height: 24,
                      borderRadius: 9999,
                      background: 'rgb(var(--fg-rgb) / 0.06)',
                      border: '1.5px solid rgb(var(--bg-rgb))',
                      fontSize: 12,
                      fontWeight: 600,
                      color: 'rgb(var(--fg-rgb) / 0.70)',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginLeft: 4,
                      flexShrink: 0,
                    }}
                  >
                    {initials}
                  </span>
                )
              })}
            </div>
            <span style={{ fontSize: 13, color: 'var(--midnight-text3)' }}>
              {record.crew.length === 1
                ? record.crew[0]
                : `${record.crew.length} crew`}
            </span>
          </div>
        )}

        {/* Materials */}
        {record.materials && record.materials.length > 0 && (
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
            <Package
              style={{
                width: 14,
                height: 14,
                color: 'var(--midnight-text3)',
                marginTop: 1,
                flexShrink: 0,
              }}
            />
            <p
              style={{
                fontSize: 13,
                color: 'var(--midnight-text3)',
                lineHeight: 1.5,
              }}
            >
              {record.materials.join(' · ')}
            </p>
          </div>
        )}

        {record.jobId && <JobMaterialsLog jobId={record.jobId} />}

        {/* Photo count */}
        {record.photoCount !== undefined && record.photoCount > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Camera
              style={{ width: 14, height: 14, color: 'var(--midnight-text3)', flexShrink: 0 }}
            />
            <span
              style={{
                fontSize: 13,
                color: 'var(--midnight-text3)',
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {record.photoCount}{' '}
              {record.photoCount === 1 ? 'photo' : 'photos'} on file
            </span>
          </div>
        )}
      </div>

      {record.jobId && <div className="mt-4"><JobPunchList key={record.jobId} jobId={record.jobId} /></div>}

      {/* Actions */}
      <div style={{ marginTop: 12, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {/*
          TODO(crew-schema): Assign crew needs `assignedToId` on Job + a Crew
          model OR JobAssignment join. Prisma schema has neither. Hidden until then.
        */}
        {/*
          TODO(time-tracking): Log time needs a TimeEntry model (hours, crew member,
          job, notes). Not in schema. Hidden until then.
        */}
        {/* Photos are added in the Photos module only (one entry point, UX-162). */}
        {record.stage === 'SCHEDULED' && onRescheduleJob && (
          <GhostButton icon={CalendarDays} label="Reschedule" onClick={onRescheduleJob} />
        )}
      </div>
      {record.jobId && <JobCostingPanel jobId={record.jobId} />}
      {record.jobId && can('costing.view') && can('jobs.view') && <Link className="btn-secondary" to={`/purchasing?job=${encodeURIComponent(record.jobId)}`}>Open buy list</Link>}
    </ModulePanel>
  )
}
